import {
  AnalyticsClientSendEventHook,
  InsightClientProvider,
  CoveoInsightClient,
  history,
} from 'coveo.analytics';
import {SearchEventRequest} from 'coveo.analytics/dist/definitions/events';
import {Logger} from 'pino';
import {
  buildFacetStateMetadata,
  getStateNeededForFacetMetadata,
  SectionNeededForFacetMetadata,
} from '../../features/facets/facet-set/facet-set-analytics-actions-utils';
import {getQueryInitialState} from '../../features/query/query-state';
import {getSearchInitialState} from '../../features/search/search-state';
import {InsightAppState} from '../../state/insight-app-state';
import {
  ConfigurationSection,
  PipelineSection,
  QuerySection,
  SearchHubSection,
  SearchSection,
} from '../../state/state-sections';
import {PreprocessRequest} from '../preprocess-request';
import {BaseAnalyticsProvider} from './base-analytics';

export type StateNeededByInsightAnalyticsProvider = ConfigurationSection &
  Partial<InsightAppState> &
  Partial<
    SearchHubSection &
      SearchSection &
      PipelineSection &
      QuerySection &
      SectionNeededForFacetMetadata
  >;

export class InsightAnalyticsProvider
  extends BaseAnalyticsProvider<StateNeededByInsightAnalyticsProvider>
  implements InsightClientProvider
{
  public getSearchUID(): string {
    const newState = this.getState();
    return (
      newState.search?.searchResponseId ||
      newState.search?.response.searchUid ||
      getSearchInitialState().response.searchUid
    );
  }
  public getPipeline(): string {
    return (
      this.state.pipeline || this.state.search?.response.pipeline || 'default'
    );
  }
  public getSearchEventRequestPayload(): Omit<
    SearchEventRequest,
    'actionCause' | 'searchQueryUid'
  > {
    return {
      queryText: this.queryText,
      responseTime: this.responseTime,
      results: this.mapResultsToAnalyticsDocument(),
      numberOfResults: this.numberOfResults,
    };
  }

  public getFacetState() {
    return buildFacetStateMetadata(getStateNeededForFacetMetadata(this.state));
  }

  private get queryText() {
    return this.state.query?.q || getQueryInitialState().q;
  }

  private get responseTime() {
    return this.state.search?.duration || getSearchInitialState().duration;
  }

  private mapResultsToAnalyticsDocument() {
    return this.state.search?.response.results.map((r) => ({
      documentUri: r.uri,
      documentUriHash: r.raw.urihash,
    }));
  }

  private get numberOfResults() {
    return (
      this.state.search?.response.results.length ||
      getSearchInitialState().response.results.length
    );
  }
}

interface ConfigureInsightAnalyticsOptions {
  logger: Logger;
  analyticsClientMiddleware?: AnalyticsClientSendEventHook;
  preprocessRequest?: PreprocessRequest;
  provider?: InsightClientProvider;
  getState(): StateNeededByInsightAnalyticsProvider;
}

export const configureInsightAnalytics = ({
  logger,
  getState,
  analyticsClientMiddleware = (_, p) => p,
  preprocessRequest,
  provider = new InsightAnalyticsProvider(getState),
}: ConfigureInsightAnalyticsOptions) => {
  const state = getState();
  const token = state.configuration.accessToken;
  const apiBaseUrl = state.configuration.analytics.apiBaseUrl;
  const runtimeEnvironment = state.configuration.analytics.runtimeEnvironment;
  const enabled = state.configuration.analytics.enabled;

  const client = new CoveoInsightClient(
    {
      enableAnalytics: enabled,
      token,
      endpoint: apiBaseUrl,
      runtimeEnvironment,
      preprocessRequest,
      beforeSendHooks: [
        analyticsClientMiddleware,
        (type, payload) => {
          logger.info(
            {
              ...payload,
              type,
              endpoint: apiBaseUrl,
              token,
            },
            'Analytics request'
          );
          return payload;
        },
      ],
    },
    provider
  );

  if (!enabled) {
    client.disable();
  }

  return client;
};

export const historyStore = new history.HistoryStore();
